import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { DateWrapper } from "./DateWrapper";
import { Link } from "react-router-dom";
import Markdown from "markdown-to-jsx";
import { htmlToPlainText } from "../../../utils/richText";
import { useSearchProjects } from "../../../customHooks/search";
import { ImageResizable } from "../../elements/ImageResizeable";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { featuredRegions, featuredCountries } from "../../../utils/constants";
import { tripLinkClick } from "../../../utils/astroTrips";

export const Filter = ({
  selectedDates,
  handleSelectDates,
  handleSelectMonths,
  handleSearch,
  closePopUp,
  handleClearSelections,
  dates,
  searchQuery: _searchQuery,
  onChangeSearch: _onChangeSearch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    searchResults,
    isSearching,
    searchQuery,
    handleSearchQueryChange,
    setSearchQuery,
    setDebouncedSearchQuery,
  } = useSearchProjects(_searchQuery, _onChangeSearch);

  const [inputFocused, setInputFocused] = useState(false);
  const searchInputRef = useRef(null);

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      {/* Search input */}
      <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-white tw-px-8 tw-pt-6 tw-pb-4">
        <div
          className="tw-flex tw-items-center tw-rounded-2xl tw-bg-white tw-px-4 tw-py-2.5 tw-relative"
          style={{
            border: inputFocused ? "1px solid #222" : "1px solid #d1d5db",
          }}
          onClick={() => searchInputRef.current?.focus()}
        >
          <input
            ref={searchInputRef}
            type="text"
            className="tw-flex-1 tw-text-sm tw-text-gray-900 tw-outline-none tw-border-none tw-bg-transparent"
            style={{ fontSize: 15, paddingRight: inputFocused ? "7rem" : "3rem" }}
            placeholder={t("search_trips_destinations_etc", {
              defaultValue: "Search trips, destinations, activities...",
            })}
            value={searchQuery}
            onChange={handleSearchQueryChange}
            onFocus={() => setInputFocused(true)}
            onBlur={(e) => {
              if (e.relatedTarget?.closest("[data-search-results-container]")) return;
              if (e.relatedTarget?.closest("[data-search-button]")) return;
              setInputFocused(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isSearching) {
                e.preventDefault();
                closePopUp();
                handleSearch();
              }
            }}
          />
          <motion.button
            type="button"
            data-search-button
            initial={{ width: "2rem" }}
            animate={inputFocused ? { width: "6rem" } : { width: "2rem" }}
            transition={{ type: "tween", duration: 0.2 }}
            className="tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-2xl tw-text-white tw-font-semibold tw-text-sm tw-cursor-pointer"
            style={{
              background: "#00bfff",
              height: "2rem",
              position: "absolute",
              right: 8,
              paddingInline: 6,
              border: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              closePopUp();
              handleSearch();
            }}
          >
            <SearchOutlinedIcon
              style={{ color: "white", width: "1.2rem", height: "1.2rem", flexShrink: 0 }}
            />
            {inputFocused && (
              <span className="tw-ml-1.5 tw-whitespace-nowrap">
                {t("search", { defaultValue: "Search" })}
              </span>
            )}
          </motion.button>
        </div>
      </div>

      {/* Content area */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-px-8 tw-pb-4">
        {inputFocused ? (
          <SearchResults
            searchResults={searchResults}
            isSearching={isSearching}
            searchQuery={searchQuery}
          />
        ) : (
          <div className="tw-flex tw-gap-8">
            {/* Left: Regions */}
            <div className="tw-flex-[3]">
              <h3 className="tw-text-base tw-font-bold tw-text-gray-900 tw-mb-4">
                {t("explore_by_region")}
              </h3>
              <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                {featuredRegions.map(([region, image, translationKey], key) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => {
                      navigate({
                        pathname: "/all-trips",
                        search: `?region=${region}`,
                      });
                      closePopUp();
                    }}
                    className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-group"
                  >
                    <div
                      className="tw-w-full tw-aspect-square tw-rounded-2xl tw-bg-gray-100 tw-overflow-hidden tw-transition-shadow group-hover:tw-shadow-md"
                      style={{
                        backgroundImage: `url('${image}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "1px solid #e5e7eb",
                      }}
                    />
                    <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
                      {t(`${translationKey}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Dates */}
            <div className="tw-flex-[2]">
              <DateWrapper
                selectedDates={selectedDates}
                handleSelectDates={handleSelectDates}
                handleSelectMonths={handleSelectMonths}
                dates={dates}
                useTailwind
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-8 tw-py-4">
        {(selectedDates.length > 0 || searchQuery) ? (
          <button
            type="button"
            onClick={() => {
              handleClearSelections();
              setSearchQuery("");
              setDebouncedSearchQuery("");
              closePopUp();
              navigate("/all-trips");
            }}
            className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-underline"
          >
            {t("clear_all", { defaultValue: "Clear all" })}
          </button>
        ) : (
          <div />
        )}

        <button
          type="button"
          disabled={isSearching}
          onClick={() => {
            closePopUp();
            handleSearch(true);
          }}
          className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-px-5 tw-py-2.5 tw-text-white tw-text-sm tw-font-semibold disabled:tw-opacity-60"
          style={{ background: "#00bfff" }}
        >
          {t("filter", { defaultValue: "Filter" })}
        </button>
      </div>
    </div>
  );
};

const SearchResults = ({ searchResults, isSearching, searchQuery }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div data-search-results-container>
      {isSearching ? (
        <div className="tw-flex tw-justify-center tw-py-8">
          <div className="tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-gray-200 tw-border-t-gray-600 tw-animate-spin" />
        </div>
      ) : searchResults.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {searchResults.map((result) => (
            <TripPreview trip={result} key={result.id} />
          ))}
        </div>
      ) : (
        <div>
          {searchQuery && (
            <p className="tw-text-center tw-text-gray-400 tw-text-sm tw-py-2">
              {t("no_results_found", { defaultValue: "No results found" })}
            </p>
          )}
          <div className="tw-mt-2">
            <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">
              {t("popular_destinations")}
            </p>
            <div className="tw-flex tw-flex-col tw-gap-1">
              {featuredCountries.map(([country, image, translationKey], key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => {
                    navigate({
                      pathname: "/all-trips",
                      search: `?destination=${country}`,
                    });
                  }}
                  className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-px-2 tw-rounded-xl tw-text-left hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <div
                    className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gray-100 tw-shrink-0"
                    style={{
                      backgroundImage: `url('${image}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                    {t(`destinations.${translationKey}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TripPreview = ({ trip: result }) => {
  return (
    <Link
      to={`/trips/${result.id}`}
      onClick={tripLinkClick(`/trips/${result.id}`, result.id, result)}
      className="tw-no-underline tw-block"
      data-search-results
    >
      <div className="tw-flex tw-items-center tw-gap-4 tw-py-2 tw-px-2 tw-rounded-xl hover:tw-bg-gray-50 tw-transition-colors">
        <ImageResizable
          src={result.thumbnail_url}
          alt={result.name}
          size="s"
          style={{
            width: 60,
            height: 60,
            objectFit: "cover",
            borderRadius: 12,
            flexShrink: 0,
          }}
        />
        <div className="tw-flex-1 tw-min-w-0">
          <p
            className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0"
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {result.name}
          </p>
          <Markdown
            style={{
              margin: 0,
              color: "#9ca3af",
              fontSize: 12,
              lineHeight: "1.4",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
            }}
          >
            {htmlToPlainText(result.summary)}
          </Markdown>
        </div>
      </div>
    </Link>
  );
};
